import { useState, useCallback, useMemo } from 'react';
import { SelectOption } from '../Select.types';
import { useDebounce } from './useDebounce';

interface UseSelectSearchProps {
  options: SelectOption[];
  searchable: boolean;
  onSearch?: (searchTerm: string) => void;
  searchDebounceMs: number;
  minSearchLength?: number;
}

/**
 * Hook for handling search functionality in the select component
**/
export const useSelectSearch = ({
  options,
  searchable: _searchable, // Renamed to indicate it's not used in this hook
  onSearch,
  searchDebounceMs,
  minSearchLength = 0,
}: UseSelectSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Debounced search handler for remote search
  const debouncedSearch = useDebounce((term: string) => {
    if (onSearch) {
      onSearch(term);
    }
  }, searchDebounceMs);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    // 🔹 sin búsqueda → mostrar todo
    if (!term) {
      return options;
    }

    // 🔹 búsqueda muy corta → mostrar todo
    if (term.length < minSearchLength) {
      return options;
    }

    // 🔍 SIEMPRE filtrar para decidir qué se muestra
    const locallyFiltered = options.filter(option =>
      option.label.toLowerCase().includes(term)
    );

    // ✅ CLAVE:
    // aunque la búsqueda sea remota, si no hay matches visibles → []
    return locallyFiltered;
  }, [options, searchTerm, minSearchLength]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    
    // Trigger remote search if available and meets minimum length
    if (onSearch) {
      if (newTerm.length >= minSearchLength) {
        debouncedSearch(newTerm);
      }
    }
  }, [onSearch, debouncedSearch, minSearchLength]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return {
    searchTerm,
    filteredOptions,
    handleSearchChange,
    clearSearch,
    setSearchTerm,
  };
};
